// src/components/Home.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Si tu proyecto NO tiene ../constants/ui o no exporta COLORS, esto evitará crash.
let COLORS_SAFE = {};
try {
  // eslint-disable-next-line global-require
  COLORS_SAFE = require("../constants/ui").COLORS || {};
} catch {
  COLORS_SAFE = {};
}

const GlobalHomeCSS = () => (
  <style>{`
    :root{
      --text: #0f172a;
      --muted: rgba(15,23,42,.72);
      --accent: #06b6d4;  /* cyan */
      --accent2:#2563eb;  /* blue */
      --ring: rgba(6,182,212,.35);
      --border: rgba(255,255,255,.22);
      --shadow: 0 18px 50px rgba(2,6,23,.18);
    }

    html, body, #root{ height:100%; }
    body{
      margin:0;
      color:var(--text);
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    }

    /* ====== Viewport safe heights (mobile browsers) ====== */
    .vh{
      min-height: 100vh;
      min-height: 100svh;
      min-height: 100dvh;
    }

    .home-root{
      position: relative;
      overflow: hidden;
    }

    .bg-slider{
      position: absolute;
      inset: 0;
      z-index: 0;
    }
    .bg-slider .slick-slider,
    .bg-slider .slick-list,
    .bg-slider .slick-track,
    .bg-slider .slick-slide > div{
      height: 100%;
    }

    .bg-slide{
      position: relative;
      width: 100%;
      height: 100%;
    }

    .bg-img{
      width:100%;
      height:100%;
      object-fit:cover;
      transform: scale(1.04);
      filter: saturate(1.05) contrast(1.02);
    }

    .bg-overlay{
      position:absolute;
      inset:0;
      background:
        radial-gradient(900px 600px at 12% 10%, rgba(37,99,235,.25), transparent 55%),
        radial-gradient(900px 600px at 88% 12%, rgba(6,182,212,.28), transparent 50%),
        radial-gradient(900px 600px at 30% 90%, rgba(99,102,241,.18), transparent 55%),
        linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.60));
    }

    .home-content{
      position: relative;
      z-index: 2;
      height: 100%;
      display: grid;
      align-items: center;
      padding: clamp(14px, 3.5vw, 36px);
    }

    .wrap{
      width: min(1180px, 92vw);
      margin: 0 auto;
    }

    .hero-grid{
      display:grid;
      gap: clamp(12px, 2.6vw, 22px);
      grid-template-columns: 1.25fr .75fr;
      align-items: start;
    }

    @media (max-width: 980px){
      .hero-grid{ grid-template-columns: 1fr; }
    }

    .card{
      background: rgba(255,255,255,.14);
      border: 1px solid var(--border);
      border-radius: 22px;
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      padding: clamp(14px, 3.2vw, 28px);
      color: #fff;
    }

    .brand{
      display:flex;
      align-items:center;
      gap: 12px;
      margin-bottom: 10px;
    }

    .logo{
      width: 44px;
      height: 44px;
      border-radius: 14px;
      background: rgba(255,255,255,.18);
      border: 1px solid rgba(255,255,255,.22);
      display:flex;
      align-items:center;
      justify-content:center;
      overflow:hidden;
      flex: 0 0 auto;
    }
    .logo img{ width:100%; height:100%; object-fit: cover; }

    .title{
      margin:0;
      font-size: clamp(24px, 4.2vw, 44px);
      font-weight: 900;
      letter-spacing: .2px;
      line-height: 1.05;
    }
    .title span{
      background: linear-gradient(90deg, var(--accent2), var(--accent));
      -webkit-background-clip: text;
      background-clip:text;
      color: transparent;
    }

    .lead{
      margin: 10px 0 0;
      font-size: clamp(14px, 1.9vw, 17px);
      line-height: 1.75;
      color: rgba(255,255,255,.88);
      max-width: 72ch;
    }

    .cta-row{
      display:flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 16px;
    }

    .btn{
      cursor:pointer;
      border: 0;
      border-radius: 14px;
      padding: 12px 14px;
      font-weight: 900;
      transition: transform .12s ease, filter .2s ease;
      display:inline-flex;
      align-items:center;
      gap: 10px;
      user-select: none;
      white-space: nowrap;
    }
    .btn:active{ transform: translateY(1px); }

    .btn-primary{
      background: linear-gradient(180deg, rgba(6,182,212,1), rgba(37,99,235,1));
      color:#062028;
      box-shadow: 0 12px 30px rgba(6,182,212,.18);
    }
    .btn-ghost{
      background: rgba(255,255,255,.14);
      color:#fff;
      border: 1px solid rgba(255,255,255,.22);
    }
    .btn:hover{ filter: brightness(1.05); }

    @media (max-width: 560px){
      .cta-row{ gap: 10px; }
      .btn{
        width: 100%;
        justify-content: center;
        padding: 12px 14px;
      }
    }

    .quick{
      display:grid;
      gap: 12px;
    }

    @media (max-width: 980px){
      .quick{ grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 560px){
      .quick{ grid-template-columns: 1fr; }
    }

    .quickCard{
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.20);
      border-radius: 18px;
      padding: 14px 14px;
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      color:#fff;
    }
    .quickTitle{ font-weight: 900; margin:0; font-size: 15px; }
    .quickText{
      margin: 6px 0 0;
      font-size: 13px;
      color: rgba(255,255,255,.84);
      line-height: 1.6;
    }

    .bg-slider .slick-dots{
      bottom: 16px;
      z-index: 6;
    }
    .bg-slider .slick-dots li button:before{
      color: rgba(255,255,255,.62);
      opacity: 1;
      font-size: 10px;
    }
    .bg-slider .slick-dots li.slick-active button:before{
      color: rgba(6,182,212,1);
    }

    .bg-slider .slick-prev, .bg-slider .slick-next{
      z-index: 7;
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: rgba(255,255,255,.16);
      border: 1px solid rgba(255,255,255,.22);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .bg-slider .slick-prev{ left: 16px; }
    .bg-slider .slick-next{ right: 16px; }
    .bg-slider .slick-prev:before, .bg-slider .slick-next:before{
      color: rgba(255,255,255,.92);
      opacity: 1;
      font-size: 20px;
    }

    @media (max-width: 560px){
      .bg-slider .slick-prev,
      .bg-slider .slick-next{ display:none !important; }
      .bg-slider .slick-dots{ bottom: 10px; }
    }

    .mini-foot{
      margin-top: 12px;
      font-size: 12px;
      color: rgba(255,255,255,.70);
    }

    @media (max-height: 680px){
      .home-content{
        align-items: start;
        padding-top: 14px;
        padding-bottom: 38px;
      }
      .wrap{ padding-bottom: 18px; }
    }
  `}</style>
);

export default function Home() {
  const navigate = useNavigate();

  const slides = useMemo(
    () => [
      {
        src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?fm=jpg&q=60&w=2400&ixlib=rb-4.0.3",
        alt: "Publicidad en vehículos",
      },
      {
        src: "https://kudeko.com/cdn/shop/products/cuadro-nueva-york-ciudad-sin-marco-fotografia-blanco-negro_800x.jpg?v=1666951353",
        alt: "Ciudad nocturna",
      },
      {
        src: "https://as2.ftcdn.net/v2/jpg/02/70/19/17/1000_F_270191793_5Nk3dvcKjuUgCuNJZ4tzL5tTNsjQlkqh.jpg",
        alt: "Tráfico urbano al atardecer",
      },
    ],
    []
  );

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 700,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4200,
      arrows: true,
      pauseOnHover: false,
      pauseOnFocus: false,
      swipe: true,
      touchMove: true,
      adaptiveHeight: false,
    }),
    []
  );

  const accent = COLORS_SAFE?.accent || "#06b6d4";

  // ✅ Detectar sesión (ajústalo a tu lógica real)
  const hasSession = useMemo(() => {
    const uid = (localStorage.getItem("uid") || "").trim();
    const username = (localStorage.getItem("username") || "").trim();
    return !!uid || !!username;
  }, []);

  // ✅ Acceso socio => Login (si no hay sesión) o Panel (si hay sesión)
  const handleAccessSocio = () => {
    if (hasSession) {
      navigate("/socio"); // panel / cuenta
    } else {
      // login (desde login tendrá link "Crear cuenta")
      navigate("/login");
      // Opcional: si prefieres ir directo a crear cuenta:
      // navigate("/signup");
    }
  };

  return (
    <>
      <GlobalHomeCSS />

      <div className="home-root vh">
        {/* ✅ Carrusel como fondo */}
        <div className="bg-slider" aria-hidden="true">
          <Slider {...settings}>
            {slides.map((s, i) => (
              <div key={i}>
                <div className="bg-slide">
                  <img className="bg-img" src={s.src} alt={s.alt || "Slide"} />
                  <div className="bg-overlay" />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* ✅ Contenido encima */}
        <div className="home-content">
          <div className="wrap">
            <div className="hero-grid">
              <div className="card">
                <div className="brand">
                  <div className="logo">
                    <img src="/screen-do-logo.png" alt="Screen DO" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 900, color: "rgba(255,255,255,.92)" }}>
                      Screen DO
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                      Publicidad dinámica • Alta visibilidad • Ingresos extra
                    </div>
                  </div>
                </div>

                <h1 className="title">
                  Publicidad en movimiento, <span>impacto real</span>.
                </h1>

                <p className="lead">
                  Conectamos marcas con miles de personas cada día mediante pantallas publicitarias
                  inteligentes instaladas en vehículos. Y si eres conductor, puedes generar ingresos extra
                  sin interrumpir tu trabajo.
                </p>

                <div className="cta-row">
                  <button className="btn btn-primary" onClick={() => navigate("/about")}>
                    🧩 Quiénes Somos
                  </button>

                  <button className="btn btn-ghost" onClick={() => navigate("/contact")}>
                    📩 Contacto
                  </button>

                  {/* ✅ AQUÍ: Acceso socio -> login o crear cuenta */}
                  <button className="btn btn-ghost" onClick={handleAccessSocio}>
                    🔐 Acceso Socio
                  </button>
                </div>

                {/* ✅ mini link para crear cuenta si no hay sesión */}
                {!hasSession && (
                  <div className="mini-foot" style={{ marginTop: 10 }}>
                    ¿Aún no tienes cuenta?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/signup")}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        color: accent,
                        fontWeight: 950,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Crear cuenta
                    </button>
                  </div>
                )}

                <div className="mini-foot">
                  Tip: tu acento actual es{" "}
                  <strong style={{ color: accent }}>{accent}</strong>
                </div>
              </div>

              <div className="quick">
                <div className="quickCard">
                  <p className="quickTitle">📍 Cobertura</p>
                  <p className="quickText">
                    Tus anuncios se muestran en zonas de alto tráfico y rutas activas durante el día.
                  </p>
                </div>
                <div className="quickCard">
                  <p className="quickTitle">🚘 Conductores</p>
                  <p className="quickText">
                    Instala la pantalla y gana mientras conduces normalmente. Sin interrupciones.
                  </p>
                </div>
                <div className="quickCard">
                  <p className="quickTitle">📈 Resultados</p>
                  <p className="quickText">
                    Mayor recordación de marca con presencia constante y visibilidad real.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ height: 18 }} />
          </div>
        </div>
      </div>
    </>
  );
}
